import { CategoryBreakdown, PopulatingDashboardCounts } from "@api";
import { Form } from "@components";
import { Box, Grid, Typography } from "@material-ui/core";
import Link from "@material-ui/core/Link";
import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core/styles";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { sub } from "date-fns";
import * as React from "react";
import { useCallback } from "react";
import { PieChart } from "react-minimal-pie-chart";
import { Link as RouterLink, useRouteMatch } from "react-router-dom";
import { getUserDashboard } from "../../../api/dashboard";
import { AttestationModal } from "../../home/modal/attestationModal";
import { init, reducer } from "../../home/state";
import { useState } from "react";
import CircularProgress from "@material-ui/core/CircularProgress/CircularProgress";

const useStyles = makeStyles({
  subtitle: {
    paddingBottom: "6px",
    "& a": {
      paddingRight: "40px",
      cursor: "pointer",
    },
    marginBottom: "20px",
  },
  paperTitles: {
    fontSize: "14px",
    fontWeight: "bold",
    paddingLeft: "15px",
    paddingTop: "10px",
  },
  paper1: {
    height: "333px",
    flexDirection: "column",
    "& li:first-child": {
      content: "•",
      color: "#ED9A2F",
    },
    "& li": {
      content: "•",
      color: "#0F7DCA",
    },
  },
  paper2: {
    height: "333px",
    borderLeftColor: "#129CEB",
    borderLeftStyle: "solid",
    borderLeftWidth: "10px",
  },
  paper3: {
    height: "333px",
    borderLeftColor: "#ED9A2F",
    borderLeftStyle: "solid",
    borderLeftWidth: "10px",
  },
  paper4: {
    height: "333px",
    borderLeftColor: "#5AA832",
    borderLeftStyle: "solid",
    borderLeftWidth: "10px",
  },
  paper5: { height: "333px" },
});

const Loading: React.FC = () => {
  return (
    <Box ml={10} mt={14}>
      <CircularProgress size="1rem" />
    </Box>
  );
};

export const Home: React.FC = () => {
  const classNames = useStyles();
  const { path } = useRouteMatch();
  const approveRequestPath = `${path}/approve-request`;
  const awaitingApprovalPath = `${path}/my-request`;
  const [state, dispatch] = React.useReducer(reducer, {}, init);
  const [dashboardValues, setDashboardValues] = React.useState<
    PopulatingDashboardCounts
  >({} as PopulatingDashboardCounts);
  const [startDate, setStartDate] = React.useState<MaterialUiPickersDate>(
    sub(new Date(), { days: 30 })
  );
  const [endDate, setEndDate] = React.useState<MaterialUiPickersDate>(
    new Date()
  );
  const [isLoading, setIsloading] = useState<boolean>(true);

  const [pieChartData, setPieChartData] = React.useState<
    {
      title: string;
      value: number;
      color: string;
    }[]
  >([]);

  React.useEffect(() => {
    getDashboardValues(startDate, endDate);
  }, []);

  const getDashboardValues = useCallback(
    (startDate: MaterialUiPickersDate, endDate: MaterialUiPickersDate) => {
      const date1 = new Date(startDate!.toString()).toISOString().slice(0, 10);
      const date2 = new Date(endDate!.toString()).toISOString().slice(0, 10);

      getUserDashboard(date1, date2)
        .then((data) => {
          setDashboardValues(data);
          let list: any = [];

          data.categoryBreakdown.forEach((value: CategoryBreakdown) => {
            if (value.category === "Application") {
              list.push({
                title: "Application",
                value: value.count,
                color: "#ED9A2F",
              });
            }
            if (value.category === "Infrastructure") {
              list.push({
                title: "Infrastructure",
                value: value.count,
                color: "#0F7DCA",
              });
            }
          });
          setPieChartData(list);
          setIsloading(false);
        })
        .catch((error) => {
          console.log("error", error);
        });
    },
    []
  );

  function handleDateValueChange(
    name: string,
    value: MaterialUiPickersDate
  ): void {
    if (name === "startDate") {
      setStartDate(value);
      getDashboardValues(value, endDate);
    }

    if (name === "endDate") {
      setEndDate(value);
      getDashboardValues(startDate, value);
    }
  }

  function handleAcceptAttestationButtonClick() {
    dispatch({
      type: "ATTESTATION_MODAL:OPEN",
    });
  }

  const closeModal = () => {
    dispatch({
      type: "ATTESTATION_MODAL:CLOSE",
    });
  };

  return (
    <React.Fragment>
      <Grid item xs={12}>
        <Box component="h3">My Tasks</Box>
      </Grid>
      <Grid item xs={12}>
        <Box className={classNames.subtitle}>
          <Link color="primary" onClick={handleAcceptAttestationButtonClick}>
            Pending for my Attestation: {dashboardValues.requestForAttestation}
          </Link>
          <Link component={RouterLink} color="primary" to={approveRequestPath}>
            Pending for my Approval: {dashboardValues.requestForApproval}
          </Link>
        </Box>
      </Grid>
      <Grid container spacing={2} item xs={12}>
        <Grid item xs={2}>
          <Paper className={classNames.paper1}>
            <Box display="flex-column">
              <Typography className={classNames.paperTitles}>
                Total Tickets: {dashboardValues.total}
              </Typography>
              {isLoading ? (
                <Loading />
              ) : (
                <React.Fragment>
                  <PieChart
                    data={pieChartData}
                    label={({ dataEntry }) =>
                      `${Math.round(dataEntry.percentage)} %`
                    }
                    labelStyle={{
                      fontSize: "8px",
                      fontFamily: "sans-serif",
                      fill: "white",
                    }}
                    radius={44}
                  />
                  <ul>
                    <li>Application</li>
                    <li>Infrastructure</li>
                  </ul>
                </React.Fragment>
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={2}>
          <Paper className={classNames.paper2}>
            <Box>
              <Typography className={classNames.paperTitles}>
                Awaiting For Approval
              </Typography>
            </Box>
            {isLoading ? (
              <Loading />
            ) : (
              <Box component="h2" pl={5} pt={10}>
                <Link
                  component={RouterLink}
                  color="primary"
                  to={awaitingApprovalPath}
                >
                  {dashboardValues.awaitingApproval}
                </Link>
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={2}>
          <Paper className={classNames.paper3}>
            <Box>
              <Typography className={classNames.paperTitles}>
                Approved
              </Typography>
              {isLoading ? (
                <Loading />
              ) : (
                <Box component="h2" pl={5} pt={10}>
                  {dashboardValues.approved}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={2}>
          <Paper className={classNames.paper4}>
            <Box>
              <Typography className={classNames.paperTitles}>
                Completed
              </Typography>
              {isLoading ? (
                <Loading />
              ) : (
                <Box component="h2" pl={5} pt={10}>
                  {dashboardValues.completed}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={2}>
          <Box className={classNames.paper5}>
            <Form>
              <Form.Group label="Filter by Date">
                <Form.Datebox
                  label={"Start Date"}
                  name="startDate"
                  onChange={handleDateValueChange}
                  size={12}
                  value={startDate}
                />
                <Form.Datebox
                  label={"End Date"}
                  name="endDate"
                  onChange={handleDateValueChange}
                  size={12}
                  value={endDate}
                />
              </Form.Group>
            </Form>
          </Box>
        </Grid>
      </Grid>
      <AttestationModal
        isOpen={state.attestation_modal.open}
        closeModal={closeModal}
      />
    </React.Fragment>
  );
};
